import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { hospitalName } from '@/utils/format';
import { BarChart3, Bell, Building, Check, CheckCheck, ChevronDown, KeyRound, Languages, LogOut, Palette, Settings } from 'lucide-react';
import { api, type Paged } from '@/api/client';
import type { Notification } from '@/api/types';
import { useLocales } from '@/api/hooks';
import { useAuth } from '@/auth/AuthContext';
import { Avatar, Button, useClickOutside, useToast } from '@/components/ui';
import { BUILTIN_LOCALES, changeLocale } from '@/i18n';
import { THEMES, useTheme } from '@/theme/ThemeProvider';
import { relativeTime } from '@/utils/format';
import { errorMessage } from '@/utils/errors';

function Dropdown({ trigger, children, label }: { trigger: React.ReactNode; children: (close: () => void) => React.ReactNode; label: string }) {
  const [open, setOpen] = useState(false);
  const ref = useClickOutside<HTMLDivElement>(() => setOpen(false));
  return (
    <div className="relative" ref={ref}>
      <button className="btn ghost" aria-label={label} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {trigger}
      </button>
      {open && <div className="popover">{children(() => setOpen(false))}</div>}
    </div>
  );
}

export function LanguageSwitcher({ compact }: { compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const { status, savePreferences } = useAuth();
  const { data } = useLocales();
  const locales = data?.length ? data : BUILTIN_LOCALES;
  const current = locales.find((l) => l.code === i18n.language);
  const select = async (code: string) => {
    await changeLocale(code, status === 'authenticated');
    if (status === 'authenticated') void savePreferences({ locale: code }).catch(() => undefined);
  };
  return (
    <Dropdown
      label={t('settings.language')}
      trigger={
        <>
          <Languages size={18} />
          {!compact && <span className="hide-mobile">{current?.nativeName ?? i18n.language}</span>}
        </>
      }
    >
      {(close) =>
        locales.map((l) => (
          <button
            key={l.code}
            className={`menu-item ${l.code === i18n.language ? 'active' : ''}`}
            lang={l.code}
            onClick={() => {
              close();
              void select(l.code);
            }}
          >
            <span style={{ flex: 1 }}>{l.nativeName}</span>
            <span className="muted small">{l.name}</span>
            {l.code === i18n.language && <Check size={16} />}
          </button>
        ))
      }
    </Dropdown>
  );
}

export function ThemeSwitcher() {
  const { t } = useTranslation();
  const { theme, setAppearance } = useTheme();
  const { status, savePreferences } = useAuth();
  const choose = (id: string) => {
    setAppearance({ theme: id });
    if (status === 'authenticated') void savePreferences({ theme: id }).catch(() => undefined);
  };
  return (
    <Dropdown label={t('settings.theme')} trigger={<Palette size={18} />}>
      {(close) => (
        <>
          {[{ id: 'system', nameKey: 'themes.system', colors: null as Record<string, string> | null }, ...THEMES].map((th) => (
            <button
              key={th.id}
              className={`menu-item ${theme === th.id ? 'active' : ''}`}
              onClick={() => {
                choose(th.id);
                close();
              }}
            >
              <span
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 6,
                  border: '1px solid var(--c-border)',
                  background: th.colors ? `linear-gradient(135deg, ${th.colors.sidebar} 50%, ${th.colors.primary} 50%)` : 'linear-gradient(135deg, #fff 50%, #111 50%)',
                }}
              />
              <span style={{ flex: 1 }}>{t(th.nameKey)}</span>
              {theme === th.id && <Check size={16} />}
            </button>
          ))}
        </>
      )}
    </Dropdown>
  );
}

export function NotificationsBell() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ['notifications', 'bell'],
    queryFn: () => api.get<Paged<Notification> & { unread: number }>('/api/v1/notifications', { pageSize: 8 }),
    refetchInterval: 30_000,
  });
  const markAll = async () => {
    await api.post('/api/v1/notifications/read-all');
    void qc.invalidateQueries({ queryKey: ['notifications'] });
  };
  const unread = data?.unread ?? 0;
  return (
    <Dropdown
      label={t('nav.notifications')}
      trigger={
        <span className="relative" style={{ display: 'inline-flex' }}>
          <Bell size={18} />
          {unread > 0 && (
            <span className="dot" style={{ top: -8, right: -10 }}>
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </span>
      }
    >
      {(close) => (
        <div style={{ width: 'min(360px, 86vw)' }}>
          <div className="row between" style={{ padding: '6px 8px' }}>
            <strong>{t('notifications.title')}</strong>
            {unread > 0 && (
              <Button icon={<CheckCheck size={14} />} size="sm" variant="ghost" onClick={markAll}>
                {t('notifications.markAllRead')}
              </Button>
            )}
          </div>
          {!data?.items.length && <div className="empty small">{t('notifications.empty')}</div>}
          {data?.items.map((n) => (
            <button
              key={n.id}
              className="menu-item"
              style={{ alignItems: 'flex-start', opacity: n.readAt ? 0.65 : 1 }}
              onClick={() => {
                close();
                void api.post(`/api/v1/notifications/${n.id}/read`).then(() => qc.invalidateQueries({ queryKey: ['notifications'] }));
                const d = n.data as Record<string, string>;
                const subject = String(n.subject);
                if (d.serviceOrderId) navigate(`/services/orders/${d.serviceOrderId}`);
                else if (subject === 'notifications.stockDocSubmitted' && d.id) navigate(`/inventory/documents/${d.id}`);
                else if ((subject === 'notifications.patientAdmitted' || subject === 'notifications.patientDischarged') && d.id) navigate(`/inpatient/admissions/${d.id}`);
                else if (subject === 'notifications.expenseSubmitted') navigate('/finance?tab=expenses&status=SUBMITTED');
                else if (subject === 'notifications.cashSessionClosed') navigate('/finance?tab=sessions');
                else if (d.prescriptionId) navigate(`/pharmacy/${d.prescriptionId}`);
                else if (d.orderId) navigate(`/lab/${d.orderId}`);
                else if (n.patientId) navigate(`/patients/${n.patientId}`);
              }}
            >
              {!n.readAt && <span style={{ width: 8, height: 8, borderRadius: 4, background: 'var(--c-primary)', marginTop: 8, flex: 'none' }} />}
              <div style={{ flex: 1 }}>
                <div>{t(n.subject, { ref: n.body, defaultValue: n.body })}</div>
                <div className="small muted">{relativeTime(n.createdAt)}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </Dropdown>
  );
}

export function HospitalSwitcher() {
  const { t, i18n } = useTranslation();
  const { session, switchHospital } = useAuth();
  const toast = useToast();
  if (!session) return null;
  if (session.hospitals.length <= 1)
    return (
      <span className="topbar-hospital hide-mobile">
        <Building size={20} /> {hospitalName(session.hospital, i18n.language)}
      </span>
    );
  return (
    <Dropdown
      label={t('auth.switchHospital')}
      trigger={
        <>
          <span className="topbar-hospital">
            <Building size={20} /> <span className="hide-mobile">{hospitalName(session.hospital, i18n.language)}</span>
          </span>{' '}
          <ChevronDown size={14} />
        </>
      }
    >
      {(close) =>
        session.hospitals.map((h) => (
          <button
            key={h.id}
            className={`menu-item ${h.id === session.hospital.id ? 'active' : ''}`}
            onClick={() => {
              close();
              if (h.id !== session.hospital.id) switchHospital(h.id).catch((e) => toast(errorMessage(e), 'error'));
            }}
          >
            <span className="stack" style={{ flex: 1, gap: 0 }}>
              <span>{hospitalName(h, i18n.language)}</span>
              {h.organizationName && <span className="small muted">{h.organizationName}</span>}
            </span>
            {h.id === session.hospital.id && <Check size={16} />}
          </button>
        ))
      }
    </Dropdown>
  );
}

export function UserMenu() {
  const { t } = useTranslation();
  const { session, logout } = useAuth();
  const navigate = useNavigate();
  if (!session) return null;
  return (
    <Dropdown label={session.user.fullName} trigger={<Avatar name={session.user.fullName} />}>
      {(close) => (
        <>
          <div style={{ padding: '8px 10px' }}>
            <strong>{session.user.fullName}</strong>
            <div className="small muted">{session.roles.map((r) => t(`enums.role.${r}`, { defaultValue: r })).join(', ')}</div>
          </div>
          <div className="divider" />
          <button
            className="menu-item"
            onClick={() => {
              close();
              navigate('/my-kpi');
            }}
          >
            <BarChart3 size={16} /> {t('staffKpi.mine')}
          </button>
          <button
            className="menu-item"
            onClick={() => {
              close();
              navigate('/settings');
            }}
          >
            <Settings size={16} /> {t('nav.settings')}
          </button>
          <button
            className="menu-item"
            onClick={() => {
              close();
              navigate('/settings#password');
            }}
          >
            <KeyRound size={16} /> {t('auth.changePassword')}
          </button>
          <button
            className="menu-item"
            onClick={() => {
              close();
              void logout().then(() => navigate('/login'));
            }}
          >
            <LogOut size={16} /> {t('nav.signOut')}
          </button>
        </>
      )}
    </Dropdown>
  );
}
